import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleMarkdownShortcut } from '../../utils/textareaMarkdownShortcuts';
import InlineMarkdown from './InlineMarkdown';

const MAX_FILES = 5;

const HiringApplicationModal = ({ isOpen, onClose, jobSlug, jobTitle }) => {
  const { t } = useTranslation();
  const [applicantName, setApplicantName] = useState('');
  const [applicantEmail, setApplicantEmail] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setError(null);
    if (picked.length + files.length > MAX_FILES) {
      setError(t('hiring.apply.errors.maxFiles', { count: MAX_FILES }));
      return;
    }
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('applicantName', applicantName.trim());
      formData.append('applicantEmail', applicantEmail.trim());
      formData.append('message', message.trim());
      formData.append('jobSlug', jobSlug || '');
      formData.append('jobTitle', jobTitle || '');
      files.forEach((f) => formData.append('cv', f));

      const res = await fetch('/api/hiring/apply', {
        method: 'POST',
        body: formData
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || t('hiring.apply.errorSend'));
      }
      setSuccess(true);
      setApplicantName('');
      setApplicantEmail('');
      setMessage('');
      setFiles([]);
    } catch (err) {
      setError(err.message || t('hiring.apply.errorSend'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(false);
    setApplicantName('');
    setApplicantEmail('');
    setMessage('');
    setFiles([]);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[240] bg-black/30 flex items-end justify-end p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="hiring-apply-title"
    >
      <div
        className="w-full max-w-lg bg-surface rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <h3 id="hiring-apply-title" className="text-base font-semibold text-ink">
            {t('hiring.apply.title')}
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="text-ink-subtle hover:text-ink text-xl leading-none"
            aria-label={t('hiring.apply.close')}
          >
            ×
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {success ? (
            <p className="text-green-700 text-sm">{t('hiring.apply.success')}</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {jobTitle && (
                <p className="text-sm text-ink-muted">
                  <span className="font-medium text-ink">{t('hiring.apply.roleLabel')}</span>{' '}
                  <InlineMarkdown as="span" variant="body">
                    {jobTitle}
                  </InlineMarkdown>
                </p>
              )}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">{t('hiring.apply.name')}</label>
                <input
                  required
                  value={applicantName}
                  onChange={(e) => setApplicantName(e.target.value)}
                  onKeyDown={(e) =>
                    handleMarkdownShortcut(e, applicantName, (next) => setApplicantName(next))
                  }
                  className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                  autoComplete="name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">{t('hiring.apply.email')}</label>
                <input
                  required
                  type="email"
                  value={applicantEmail}
                  onChange={(e) => setApplicantEmail(e.target.value)}
                  className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">{t('hiring.apply.message')}</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => handleMarkdownShortcut(e, message, (next) => setMessage(next))}
                  rows={4}
                  className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                  placeholder={t('hiring.apply.messagePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">{t('hiring.apply.cvLabel')}</label>
                <p className="text-xs text-ink-subtle mb-2">
                  {t('hiring.apply.cvHint', { max: MAX_FILES })}
                </p>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  multiple
                  onChange={handleFiles}
                  className="text-sm w-full"
                />
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 bg-canvas rounded px-2 py-1">
                        <span className="truncate">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="text-red-600 hover:text-red-800 shrink-0"
                        >
                          {t('hiring.apply.removeFile')}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 rounded-lg border border-edge-strong text-ink-muted hover:bg-surface-muted text-sm"
                >
                  {t('hiring.apply.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-hover text-primary-foreground text-sm disabled:opacity-50"
                >
                  {submitting ? t('hiring.apply.sending') : t('hiring.apply.send')}
                </button>
              </div>
            </form>
          )}
        </div>
        {success && (
          <div className="p-4 border-t flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-hover text-primary-foreground text-sm"
            >
              {t('hiring.apply.done')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HiringApplicationModal;
